import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { useToast } from '../../../components/ui/use-toast';
import { supabase } from '../../../lib/supabase';
import { Image, X, Calendar, DollarSign, Trophy, Loader2, Facebook, Twitter, Instagram, ArrowLeft, Home, Newspaper, BarChart2, HelpCircle, Award, Info, ArrowLeft as ArrowLeftIcon, ArrowRight as ArrowRightIcon } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { useCheckPermissions } from '../../../middleware/checkPermissions';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const contestSchema = z.object({
  name: z.string().min(1, 'Le nom est requis'),
  description: z.string().min(1, 'La description est requise'),
  vote_price: z.coerce.number().min(0, 'Le prix doit être positif'),
  commission_percentage: z.coerce.number().min(0, 'La commission doit être positive').max(100, 'La commission ne peut pas dépasser 100%'),
  start_date: z.string().min(1, 'La date de début est requise'),
  end_date: z.string().min(1, 'La date de fin est requise'),
  status: z.enum(['draft', 'active', 'ended']),
  code_unique: z.string().min(1, 'Le code unique est requis'),
  title_color_from: z.string().default('#FEFCE8'),
  title_color_to: z.string().default('#FDE047'),
  description_color: z.string().default('#FEF9C3'),
  description_opacity: z.string().default('0.8'),
  header_bg_from: z.string().default('#000000'),
  header_bg_to: z.string().default('#000000'),
  header_bg_opacity_from: z.string().default('0.9'),
  header_bg_opacity_to: z.string().default('0.5'),
  logo_glow_from: z.string().default('#F59E0B'),
  logo_glow_via: z.string().default('#FCD34D'),
  logo_glow_to: z.string().default('#F59E0B'),
  participation_section_title: z.string().default('Créez ou participez'),
  participation_section_description: z.string().default('Que vous souhaitiez organiser un concours ou participer à ceux à venir, VotAfrica vous offre une plateforme complète pour vivre des expériences uniques et gagner des prix exceptionnels.'),
  participation_primary_button_text: z.string().default('Soumettre un concours'),
  participation_primary_button_link: z.string().default('/soumettre-concours'),
  participation_secondary_button_text: z.string().default('Participer à un concours'),
  participation_secondary_button_link: z.string().default('/upcoming-contests'),
  contact_info: z.object({
    phones: z.string().default('+237 698175473, +237 697058963, +237 653584838'),
    email: z.string().email().default('contact@votafrica.com'),
    address: z.string().default('Douala, Cameroun'),
    icon_color: z.string().default('#F59E0B'),
    text_color: z.string().default('#D1D5DB'),
    hover_color: z.string().default('#FDE047'),
    social_links: z.object({
      facebook: z.string().url().default('https://facebook.com/votafrica'),
      twitter: z.string().url().default('https://twitter.com/votafrica'),
      instagram: z.string().url().default('https://instagram.com/votafrica'),
      icon_color: z.string().default('#D1D5DB'),
      hover_color: z.string().default('#FDE047')
    }).default({
      facebook: 'https://facebook.com/votafrica',
      twitter: 'https://twitter.com/votafrica',
      instagram: 'https://instagram.com/votafrica',
      icon_color: '#D1D5DB',
      hover_color: '#FDE047'
    })
  }).default({
    phones: '+237 698175473, +237 697058963, +237 653584838',
    email: 'contact@votafrica.com',
    address: 'Douala, Cameroun',
    icon_color: '#F59E0B',
    text_color: '#D1D5DB',
    hover_color: '#FDE047',
    social_links: {
      facebook: 'https://facebook.com/votafrica',
      twitter: 'https://twitter.com/votafrica',
      instagram: 'https://instagram.com/votafrica',
      icon_color: '#D1D5DB',
      hover_color: '#FDE047'
    }
  }),
  menu_items: z.array(z.object({
    label: z.string(),
    href: z.string(),
    variant: z.enum(['link', 'button']).default('link'),
    color: z.string().default('#D1D5DB'),
    hover_color: z.string().default('#FDE047'),
    icon: z.string().optional(),
  })).default([
    { label: 'Accueil', href: '/', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'Home' },
    { label: 'Concours', href: '/contests', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'Trophy' },
    { label: 'Blog', href: '/blog', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'Newspaper' },
    { label: 'Classement', href: '/classement', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'BarChart2' },
    { label: 'FAQ', href: '/faq', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'HelpCircle' },
    { label: 'Awards', href: '/awards', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'Award' },
    { label: 'À propos', href: '/about', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'Info' },
  ]),
  cover: z
    .instanceof(FileList)
    .optional()
    .refine((files) => {
      if (files && files.length > 0) {
        return files[0].size <= MAX_FILE_SIZE;
      }
      return true;
    }, 'La taille maximale du fichier est de 5MB')
    .refine((files) => {
      if (files && files.length > 0) {
        return ACCEPTED_IMAGE_TYPES.includes(files[0].type);
      }
      return true;
    }, 'Format accepté : .jpg, .jpeg, .png, .webp'),
  logo: z
    .instanceof(FileList)
    .optional()
    .refine((files) => {
      if (files && files.length > 0) {
        return files[0].size <= MAX_FILE_SIZE;
      }
      return true;
    }, 'La taille maximale du fichier est de 5MB')
    .refine((files) => {
      if (files && files.length > 0) {
        return ACCEPTED_IMAGE_TYPES.includes(files[0].type);
      }
      return true;
    }, 'Format accepté : .jpg, .jpeg, .png, .webp'),
  partner_logos: z.array(z.string()).optional(),
});

type ContestFormValues = z.infer<typeof contestSchema>;

export const EditContestPage = () => {
  useCheckPermissions(['admin']);
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [contest, setContest] = useState<any>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [partnerLogoInput, setPartnerLogoInput] = useState<string | File | File[]>("");

  const form = useForm<ContestFormValues>({
    resolver: zodResolver(contestSchema),
    defaultValues: {
      name: '',
      description: '',
      vote_price: 0,
      commission_percentage: 10,
      start_date: '',
      end_date: '',
      status: 'draft' as const,
      code_unique: '',
      title_color_from: '#FEFCE8',
      title_color_to: '#FDE047',
      description_color: '#FEF9C3',
      description_opacity: '0.8',
      header_bg_from: '#000000',
      header_bg_to: '#000000',
      header_bg_opacity_from: '0.9',
      header_bg_opacity_to: '0.5',
      logo_glow_from: '#F59E0B',
      logo_glow_via: '#FCD34D',
      logo_glow_to: '#F59E0B',
      participation_section_title: 'Créez ou participez',
      participation_section_description: 'Que vous souhaitiez organiser un concours ou participer à ceux à venir, VotAfrica vous offre une plateforme complète pour vivre des expériences uniques et gagner des prix exceptionnels.',
      participation_primary_button_text: 'Soumettre un concours',
      participation_primary_button_link: '/soumettre-concours',
      participation_secondary_button_text: 'Participer à un concours',
      participation_secondary_button_link: '/upcoming-contests',
      contact_info: {
        phones: '+237 698175473, +237 697058963, +237 653584838',
        email: 'contact@votafrica.com',
        address: 'Douala, Cameroun',
        icon_color: '#F59E0B',
        text_color: '#D1D5DB',
        hover_color: '#FDE047',
        social_links: {
          facebook: 'https://facebook.com/votafrica',
          twitter: 'https://twitter.com/votafrica',
          instagram: 'https://instagram.com/votafrica',
          icon_color: '#D1D5DB',
          hover_color: '#FDE047'
        }
      },
      menu_items: [
        { label: 'Accueil', href: '/', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'Home' },
        { label: 'Concours', href: '/contests', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'Trophy' },
        { label: 'Blog', href: '/blog', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'Newspaper' },
        { label: 'Classement', href: '/classement', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'BarChart2' },
        { label: 'FAQ', href: '/faq', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'HelpCircle' },
        { label: 'Awards', href: '/awards', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'Award' },
        { label: 'À propos', href: '/about', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'Info' },
      ],
      partner_logos: contest?.partner_logos || [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "menu_items",
  });

  const { register } = form;

  useEffect(() => {
    const fetchContest = async () => {
      if (!id) {
        setLoading(false);
        navigate('/admin/contests');
        return;
      }
      
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('contests')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        if (!data) {
          toast({
            variant: 'destructive',
            title: 'Erreur',
            description: 'Concours non trouvé',
          });
          navigate('/admin/contests');
          return;
        }

        setContest(data);
        setImagePreview(data.photo_url || null);
        setLogoPreview(data.logo_url || null);

        form.reset({
          name: data.name,
          description: data.description,
          vote_price: data.vote_price,
          commission_percentage: data.commission_percentage || 10,
          start_date: new Date(data.start_date).toISOString().slice(0, 16),
          end_date: new Date(data.end_date).toISOString().slice(0, 16),
          status: data.status as 'draft' | 'active' | 'ended',
          code_unique: data.code_unique,
          title_color_from: data.theme_config?.title_color_from || '#FEFCE8',
          title_color_to: data.theme_config?.title_color_to || '#FDE047',
          description_color: data.theme_config?.description_color || '#FEF9C3',
          description_opacity: data.theme_config?.description_opacity || '0.8',
          header_bg_from: data.theme_config?.header_bg_from || '#000000',
          header_bg_to: data.theme_config?.header_bg_to || '#000000',
          header_bg_opacity_from: data.theme_config?.header_bg_opacity_from || '0.9',
          header_bg_opacity_to: data.theme_config?.header_bg_opacity_to || '0.5',
          logo_glow_from: data.theme_config?.logo_glow_from || '#F59E0B',
          logo_glow_via: data.theme_config?.logo_glow_via || '#FCD34D',
          logo_glow_to: data.theme_config?.logo_glow_to || '#F59E0B',
          participation_section_title: data.theme_config?.participation_section_title || 'Créez ou participez',
          participation_section_description: data.theme_config?.participation_section_description || 'Que vous souhaitiez organiser un concours ou participer à ceux à venir, VotAfrica vous offre une plateforme complète pour vivre des expériences uniques et gagner des prix exceptionnels.',
          participation_primary_button_text: data.theme_config?.participation_primary_button_text || 'Soumettre un concours',
          participation_primary_button_link: data.theme_config?.participation_primary_button_link || '/soumettre-concours',
          participation_secondary_button_text: data.theme_config?.participation_secondary_button_text || 'Participer à un concours',
          participation_secondary_button_link: data.theme_config?.participation_secondary_button_link || '/upcoming-contests',
          contact_info: data.theme_config?.contact_info || {
            phones: '+237 698175473, +237 697058963, +237 653584838',
            email: 'contact@votafrica.com',
            address: 'Douala, Cameroun',
            icon_color: '#F59E0B',
            text_color: '#D1D5DB',
            hover_color: '#FDE047',
            social_links: {
              facebook: 'https://facebook.com/votafrica',
              twitter: 'https://twitter.com/votafrica',
              instagram: 'https://instagram.com/votafrica',
              icon_color: '#D1D5DB',
              hover_color: '#FDE047'
            }
          },
          menu_items: data.theme_config?.menu_items || [
            { label: 'Accueil', href: '/', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'Home' },
            { label: 'Concours', href: '/contests', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'Trophy' },
            { label: 'Blog', href: '/blog', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'Newspaper' },
            { label: 'Classement', href: '/classement', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'BarChart2' },
            { label: 'FAQ', href: '/faq', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'HelpCircle' },
            { label: 'Awards', href: '/awards', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'Award' },
            { label: 'À propos', href: '/about', variant: 'link', color: '#D1D5DB', hover_color: '#FDE047', icon: 'Info' },
          ],
          partner_logos: data.partner_logos || [],
        });
      } catch (error: any) {
        console.error('Erreur lors de la récupération du concours:', error);
        toast({
          variant: 'destructive',
          title: 'Erreur',
          description: 'Impossible de charger le concours',
        });
        navigate('/admin/contests');
      } finally {
        setLoading(false);
      }
    };

    fetchContest();
  }, [id, navigate, form, toast]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview(contest?.photo_url || null);
    }
  };

  const handleDeleteImage = async () => {
    try {
      setIsDeleting(true);
      
      // Supprimer l'image de Supabase Storage
      if (contest?.photo_url) {
        const fileName = contest.photo_url.split('/').pop();
        if (fileName) {
          await supabase.storage
            .from('candidates')
            .remove([fileName]);
        }
      }

      // Mettre à jour le concours sans l'URL de l'image
      const { error } = await supabase
        .from('contests')
        .update({ photo_url: null })
        .eq('id', contest.id);

      if (error) throw error;

      setImagePreview(null);
      toast({
        title: 'Succès',
        description: "L'image a été supprimée",
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.message || "Impossible de supprimer l'image",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${contest.id}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('candidates')
      .upload(filePath, file, {
        upsert: true,
      });

    if (uploadError) {
      throw uploadError;
    }

    const { data } = supabase.storage
      .from('candidates')
      .getPublicUrl(filePath);

    return data.publicUrl;
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setLogoPreview(contest?.logo_url || null);
    }
  };

  const handleDeleteLogo = async () => {
    try {
      setIsDeleting(true);
      
      if (contest?.logo_url) {
        const fileName = contest.logo_url.split('/').pop();
        if (fileName) {
          await supabase.storage
            .from('candidates')
            .remove([fileName]);
        }
      }

      const { error } = await supabase
        .from('contests')
        .update({ logo_url: null })
        .eq('id', contest.id);

      if (error) throw error;

      setLogoPreview(null);
      toast({
        title: 'Succès',
        description: "Le logo a été supprimé",
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.message || "Impossible de supprimer le logo",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const uploadLogo = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `logo-${contest.id}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('candidates')
      .upload(filePath, file, {
        upsert: true,
      });

    if (uploadError) {
      throw uploadError;
    }

    const { data } = supabase.storage
      .from('candidates')
      .getPublicUrl(filePath);

    return data.publicUrl;
  };

  const addPartnerLogo = async () => {
    if (!partnerLogoInput || (typeof partnerLogoInput === 'string' && !partnerLogoInput.trim())) return;
    
    setIsUploading(true);
    try {
      let urls: string[] = [];
      // Cas 1 : Plusieurs fichiers
      if (Array.isArray(partnerLogoInput)) {
        for (const file of partnerLogoInput) {
          // Validation taille
          if (file.size > MAX_FILE_SIZE) {
            toast({ title: 'Fichier trop volumineux', description: 'La taille maximale autorisée est de 5MB', variant: 'destructive' });
            continue;
          }
          // Validation type
          if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
            toast({ title: 'Format non supporté', description: 'Formats acceptés : JPG, PNG, WebP', variant: 'destructive' });
            continue;
          }
          const fileExt = file.name.split('.').pop();
          const fileName = `partner_${contest?.id || 'new'}_${Date.now()}_${Math.random().toString(36).slice(2)}.${fileExt}`;
          const { error } = await supabase.storage.from('partners').upload(fileName, file, { upsert: true });
          if (error) {
            toast({ title: 'Erreur', description: "Impossible d'uploader le logo partenaire: " + error.message, variant: 'destructive' });
            continue;
          }
          const { data } = supabase.storage.from('partners').getPublicUrl(fileName);
          urls.push(data.publicUrl);
        }
      }
      // Cas 2 : Un seul fichier
      else if (partnerLogoInput instanceof File) {
        const file = partnerLogoInput;
        if (file.size > MAX_FILE_SIZE) {
          toast({ title: 'Fichier trop volumineux', description: 'La taille maximale autorisée est de 5MB', variant: 'destructive' });
          setIsUploading(false);
          return;
        }
        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
          toast({ title: 'Format non supporté', description: 'Formats acceptés : JPG, PNG, WebP', variant: 'destructive' });
          setIsUploading(false);
          return;
        }
        const fileExt = file.name.split('.').pop();
        const fileName = `partner_${contest?.id || 'new'}_${Date.now()}.${fileExt}`;
        const { error } = await supabase.storage.from('partners').upload(fileName, file, { upsert: true });
        if (error) {
          toast({ title: 'Erreur', description: "Impossible d'uploader le logo partenaire: " + error.message, variant: 'destructive' });
          setIsUploading(false);
          return;
        }
        const { data } = supabase.storage.from('partners').getPublicUrl(fileName);
        urls.push(data.publicUrl);
      }
      // Cas 3 : Plusieurs URLs séparées par des virgules
      else if (typeof partnerLogoInput === 'string' && partnerLogoInput.includes(',')) {
        const urlList = partnerLogoInput.split(',').map(u => u.trim()).filter(Boolean);
        for (const url of urlList) {
          try {
            new URL(url);
            urls.push(url);
          } catch (e) {
            toast({ title: 'URL invalide', description: `URL invalide: ${url}`, variant: 'destructive' });
          }
        }
      }
      // Cas 4 : Une seule URL
      else if (typeof partnerLogoInput === 'string') {
        let url = partnerLogoInput.trim();
        try {
          new URL(url);
          urls.push(url);
        } catch (e) {
          toast({ title: 'URL invalide', description: "Veuillez entrer une URL valide", variant: 'destructive' });
          setIsUploading(false);
          return;
        }
      }
      // Ajout dans le formulaire
      if (urls.length > 0) {
        const currentLogos = form.getValues('partner_logos') || [];
        form.setValue('partner_logos', [...currentLogos, ...urls]);
        setPartnerLogoInput("");
        toast({ title: 'Logo(s) ajouté(s)', description: "Les logos partenaires ont été ajoutés avec succès", variant: 'default' });
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast({ title: 'Erreur', description: "Une erreur est survenue lors de l'ajout du/des logo(s)", variant: 'destructive' });
    } finally {
      setIsUploading(false);
    }
  };

  const removePartnerLogo = (index: number) => {
    const currentLogos = form.getValues('partner_logos') || [];
    form.setValue('partner_logos', currentLogos.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: ContestFormValues) => {
    try {
      setIsUploading(true);

      // Upload cover image if provided
      let photoUrl = contest?.photo_url || null;
      if (data.cover && data.cover.length > 0) {
        photoUrl = await uploadImage(data.cover[0]);
      }

      // Upload logo if provided
      let logoUrl = contest?.logo_url || null;
      if (data.logo && data.logo.length > 0) {
        logoUrl = await uploadLogo(data.logo[0]);
      }

      // Prepare theme config
      const themeConfig = {
        title_color_from: data.title_color_from,
        title_color_to: data.title_color_to,
        description_color: data.description_color,
        description_opacity: data.description_opacity,
        header_bg_from: data.header_bg_from,
        header_bg_to: data.header_bg_to,
        header_bg_opacity_from: data.header_bg_opacity_from,
        header_bg_opacity_to: data.header_bg_opacity_to,
        logo_glow_from: data.logo_glow_from,
        logo_glow_via: data.logo_glow_via,
        logo_glow_to: data.logo_glow_to,
        participation_section_title: data.participation_section_title,
        participation_section_description: data.participation_section_description,
        participation_primary_button_text: data.participation_primary_button_text,
        participation_primary_button_link: data.participation_primary_button_link,
        participation_secondary_button_text: data.participation_secondary_button_text,
        participation_secondary_button_link: data.participation_secondary_button_link,
        contact_info: data.contact_info,
        menu_items: data.menu_items,
      };

      // Update contest
      const { error } = await supabase
        .from('contests')
        .update({
          name: data.name,
          description: data.description,
          vote_price: data.vote_price,
          commission_percentage: data.commission_percentage,
          start_date: data.start_date,
          end_date: data.end_date,
          status: data.status,
          code_unique: data.code_unique,
          photo_url: photoUrl,
          logo_url: logoUrl,
          theme_config: themeConfig,
          partner_logos: data.partner_logos,
        })
        .eq('id', contest.id);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: 'Le concours a été mis à jour',
      });

      // Redirect to contests list
      navigate('/admin/contests');
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour du concours:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.message || 'Impossible de mettre à jour le concours',
      });
    } finally {
      setIsUploading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-yellow-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="icon" 
              onClick={() => navigate('/admin/contests')}
              className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold">
              <span className="bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 text-transparent bg-clip-text">
                Modifier le concours
              </span>
            </h1>
          </div>
          <Button
            type="submit"
            form="contest-form"
            disabled={isUploading}
            className="bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-500 hover:to-yellow-400 text-white shadow-lg shadow-yellow-500/20 hover:shadow-yellow-500/40"
          >
            {isUploading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Mise à jour...
              </div>
            ) : (
              <span className="text-white">Mettre à jour</span>
            )}
          </Button>
        </div>

        <Form {...form}>
          <form id="contest-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-medium mb-4 text-gray-200">Informations générales</h3>
                    <div className="space-y-4">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-200">Nom du concours</FormLabel>
                            <FormControl>
                              <Input 
                                {...field} 
                                className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                              />
                            </FormControl>
                            <FormMessage className="text-red-400" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-200">Description</FormLabel>
                            <FormControl>
                              <Textarea 
                                {...field} 
                                rows={4}
                                className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                              />
                            </FormControl>
                            <FormMessage className="text-red-400" />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="start_date"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-gray-200">Date de début</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Calendar className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                                  <Input 
                                    type="datetime-local" 
                                    {...field} 
                                    className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                  />
                                </div>
                              </FormControl>
                              <FormMessage className="text-red-400" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="end_date"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-gray-200">Date de fin</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Calendar className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                                  <Input 
                                    type="datetime-local" 
                                    {...field} 
                                    className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                  />
                                </div>
                              </FormControl>
                              <FormMessage className="text-red-400" />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="vote_price"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-gray-200">Prix du vote (FCFA)</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <DollarSign className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                                  <Input 
                                    type="number" 
                                    {...field} 
                                    className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                  />
                                </div>
                              </FormControl>
                              <FormMessage className="text-red-400" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="commission_percentage"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-gray-200">Commission (%)</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <span className="absolute left-3 top-3 text-gray-400">%</span>
                                  <Input 
                                    type="number" 
                                    {...field} 
                                    className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                  />
                                </div>
                              </FormControl>
                              <FormMessage className="text-red-400" />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="status"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-200">Statut</FormLabel>
                            <FormControl>
                              <select 
                                {...field} 
                                className="w-full bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50 rounded-md"
                              >
                                <option value="draft">Brouillon</option>
                                <option value="active">Actif</option>
                                <option value="ended">Terminé</option>
                              </select>
                            </FormControl>
                            <FormMessage className="text-red-400" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="code_unique"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-200">Code unique</FormLabel>
                            <FormControl>
                              <Input 
                                {...field} 
                                className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                              />
                            </FormControl>
                            <FormMessage className="text-red-400" />
                          </FormItem>
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-medium mb-4 text-gray-200">Personnalisation du thème</h3>
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-medium text-gray-300 mb-3">Titre</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="title_color_from"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-200">Couleur de début</FormLabel>
                                <FormControl>
                                  <div className="flex items-center space-x-2">
                                    <Input 
                                      type="color" 
                                      {...field} 
                                      className="w-20 h-10 bg-white/5 border-white/10" 
                                    />
                                    <Input 
                                      {...field} 
                                      className="flex-1 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage className="text-red-400" />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="title_color_to"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-200">Couleur de fin</FormLabel>
                                <FormControl>
                                  <div className="flex items-center space-x-2">
                                    <Input 
                                      type="color" 
                                      {...field} 
                                      className="w-20 h-10 bg-white/5 border-white/10" 
                                    />
                                    <Input 
                                      {...field} 
                                      className="flex-1 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage className="text-red-400" />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium text-gray-300 mb-3">Description</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="description_color"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-200">Couleur</FormLabel>
                                <FormControl>
                                  <div className="flex items-center space-x-2">
                                    <Input 
                                      type="color" 
                                      {...field} 
                                      className="w-20 h-10 bg-white/5 border-white/10" 
                                    />
                                    <Input 
                                      {...field} 
                                      className="flex-1 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage className="text-red-400" />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="description_opacity"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-200">Opacité</FormLabel>
                                <FormControl>
                                  <Input 
                                    type="number" 
                                    min="0" 
                                    max="1" 
                                    step="0.1" 
                                    {...field} 
                                    className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                  />
                                </FormControl>
                                <FormMessage className="text-red-400" />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium text-gray-300 mb-3">Arrière-plan de l'en-tête</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="header_bg_from"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-200">Couleur de début</FormLabel>
                                <FormControl>
                                  <div className="flex items-center space-x-2">
                                    <Input 
                                      type="color" 
                                      {...field} 
                                      className="w-20 h-10 bg-white/5 border-white/10" 
                                    />
                                    <Input 
                                      {...field} 
                                      className="flex-1 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage className="text-red-400" />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="header_bg_to"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-200">Couleur de fin</FormLabel>
                                <FormControl>
                                  <div className="flex items-center space-x-2">
                                    <Input 
                                      type="color" 
                                      {...field} 
                                      className="w-20 h-10 bg-white/5 border-white/10" 
                                    />
                                    <Input 
                                      {...field} 
                                      className="flex-1 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage className="text-red-400" />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium text-gray-300 mb-3">Opacité de l'arrière-plan</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="header_bg_opacity_from"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-200">Opacité de début</FormLabel>
                                <FormControl>
                                  <Input 
                                    type="number" 
                                    min="0" 
                                    max="1" 
                                    step="0.1" 
                                    {...field} 
                                    className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                  />
                                </FormControl>
                                <FormMessage className="text-red-400" />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="header_bg_opacity_to"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-200">Opacité de fin</FormLabel>
                                <FormControl>
                                  <Input 
                                    type="number" 
                                    min="0" 
                                    max="1" 
                                    step="0.1" 
                                    {...field} 
                                    className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                  />
                                </FormControl>
                                <FormMessage className="text-red-400" />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium text-gray-300 mb-3">Lueur du logo</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <FormField
                            control={form.control}
                            name="logo_glow_from"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-200">Couleur de début</FormLabel>
                                <FormControl>
                                  <div className="flex items-center space-x-2">
                                    <Input 
                                      type="color" 
                                      {...field} 
                                      className="w-20 h-10 bg-white/5 border-white/10" 
                                    />
                                    <Input 
                                      {...field} 
                                      className="flex-1 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage className="text-red-400" />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="logo_glow_via"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-200">Couleur intermédiaire</FormLabel>
                                <FormControl>
                                  <div className="flex items-center space-x-2">
                                    <Input 
                                      type="color" 
                                      {...field} 
                                      className="w-20 h-10 bg-white/5 border-white/10" 
                                    />
                                    <Input 
                                      {...field} 
                                      className="flex-1 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage className="text-red-400" />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="logo_glow_to"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-200">Couleur de fin</FormLabel>
                                <FormControl>
                                  <div className="flex items-center space-x-2">
                                    <Input 
                                      type="color" 
                                      {...field} 
                                      className="w-20 h-10 bg-white/5 border-white/10" 
                                    />
                                    <Input 
                                      {...field} 
                                      className="flex-1 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage className="text-red-400" />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-medium mb-4 text-gray-200">Informations de contact</h3>
                    <div className="space-y-6">
                      <FormField
                        control={form.control}
                        name="contact_info.phones"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-200">Téléphones</FormLabel>
                            <FormControl>
                              <Input 
                                {...field} 
                                className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                              />
                            </FormControl>
                            <FormDescription className="text-gray-400 text-sm">
                              Séparez les numéros par des virgules
                            </FormDescription>
                            <FormMessage className="text-red-400" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="contact_info.email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-200">Email</FormLabel>
                            <FormControl>
                              <Input 
                                type="email" 
                                {...field} 
                                className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                              />
                            </FormControl>
                            <FormMessage className="text-red-400" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="contact_info.address"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-200">Adresse</FormLabel>
                            <FormControl>
                              <Input 
                                {...field} 
                                className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                              />
                            </FormControl>
                            <FormMessage className="text-red-400" />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField
                          control={form.control}
                          name="contact_info.icon_color"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-gray-200">Couleur des icônes</FormLabel>
                              <FormControl>
                                <div className="flex items-center space-x-2">
                                  <Input 
                                    type="color" 
                                    {...field} 
                                    className="w-20 h-10 bg-white/5 border-white/10" 
                                  />
                                  <Input 
                                    {...field} 
                                    className="flex-1 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                  />
                                </div>
                              </FormControl>
                              <FormMessage className="text-red-400" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="contact_info.text_color"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-gray-200">Couleur du texte</FormLabel>
                              <FormControl>
                                <div className="flex items-center space-x-2">
                                  <Input 
                                    type="color" 
                                    {...field} 
                                    className="w-20 h-10 bg-white/5 border-white/10" 
                                  />
                                  <Input 
                                    {...field} 
                                    className="flex-1 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                  />
                                </div>
                              </FormControl>
                              <FormMessage className="text-red-400" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="contact_info.hover_color"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-gray-200">Couleur au survol</FormLabel>
                              <FormControl>
                                <div className="flex items-center space-x-2">
                                  <Input 
                                    type="color" 
                                    {...field} 
                                    className="w-20 h-10 bg-white/5 border-white/10" 
                                  />
                                  <Input 
                                    {...field} 
                                    className="flex-1 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                  />
                                </div>
                              </FormControl>
                              <FormMessage className="text-red-400" />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-medium mb-4 text-gray-200">Réseaux sociaux</h3>
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField
                          control={form.control}
                          name="contact_info.social_links.facebook"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-gray-200">Facebook</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Facebook className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                                  <Input 
                                    {...field} 
                                    className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                  />
                                </div>
                              </FormControl>
                              <FormMessage className="text-red-400" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="contact_info.social_links.twitter"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-gray-200">Twitter</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Twitter className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                                  <Input 
                                    {...field} 
                                    className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                  />
                                </div>
                              </FormControl>
                              <FormMessage className="text-red-400" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="contact_info.social_links.instagram"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-gray-200">Instagram</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Instagram className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                                  <Input 
                                    {...field} 
                                    className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                  />
                                </div>
                              </FormControl>
                              <FormMessage className="text-red-400" />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="contact_info.social_links.icon_color"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-gray-200">Couleur des icônes</FormLabel>
                              <FormControl>
                                <div className="flex items-center space-x-2">
                                  <Input 
                                    type="color" 
                                    {...field} 
                                    className="w-20 h-10 bg-white/5 border-white/10" 
                                  />
                                  <Input 
                                    {...field} 
                                    className="flex-1 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                  />
                                </div>
                              </FormControl>
                              <FormMessage className="text-red-400" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="contact_info.social_links.hover_color"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-gray-200">Couleur au survol</FormLabel>
                              <FormControl>
                                <div className="flex items-center space-x-2">
                                  <Input 
                                    type="color" 
                                    {...field} 
                                    className="w-20 h-10 bg-white/5 border-white/10" 
                                  />
                                  <Input 
                                    {...field} 
                                    className="flex-1 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                  />
                                </div>
                              </FormControl>
                              <FormMessage className="text-red-400" />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-medium mb-4 text-gray-200">Section "Créez ou participez"</h3>
                    <div className="space-y-6">
                      <FormField
                        control={form.control}
                        name="participation_section_title"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-200">Titre de la section</FormLabel>
                            <FormControl>
                              <Input 
                                {...field} 
                                placeholder="Créez ou participez"
                                className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                              />
                            </FormControl>
                            <FormMessage className="text-red-400" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="participation_section_description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-200">Description</FormLabel>
                            <FormControl>
                              <Textarea 
                                {...field} 
                                placeholder="Que vous souhaitiez organiser un concours ou participer à ceux à venir, VotAfrica vous offre une plateforme complète pour vivre des expériences uniques et gagner des prix exceptionnels."
                                className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50 min-h-[100px]" 
                              />
                            </FormControl>
                            <FormMessage className="text-red-400" />
                          </FormItem>
                        )}
                      />

                      <div className="space-y-4">
                        <h4 className="text-md font-medium text-gray-300">Bouton principal</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="participation_primary_button_text"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-200">Texte</FormLabel>
                                <FormControl>
                                  <Input 
                                    {...field} 
                                    placeholder="Soumettre un concours"
                                    className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                  />
                                </FormControl>
                                <FormMessage className="text-red-400" />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="participation_primary_button_link"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-200">Lien</FormLabel>
                                <FormControl>
                                  <Input 
                                    {...field} 
                                    placeholder="/soumettre-concours"
                                    className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                  />
                                </FormControl>
                                <FormMessage className="text-red-400" />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      <div className="space-y-4">
                        <h4 className="text-md font-medium text-gray-300">Bouton secondaire</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="participation_secondary_button_text"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-200">Texte</FormLabel>
                                <FormControl>
                                  <Input 
                                    {...field} 
                                    placeholder="Participer à un concours"
                                    className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                  />
                                </FormControl>
                                <FormMessage className="text-red-400" />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="participation_secondary_button_link"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-200">Lien</FormLabel>
                                <FormControl>
                                  <Input 
                                    {...field} 
                                    placeholder="/upcoming-contests"
                                    className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                  />
                                </FormControl>
                                <FormMessage className="text-red-400" />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-200">Personnalisation du menu</h3>
                      <Button
                        type="button"
                        onClick={() => append({ 
                          label: '', 
                          href: '', 
                          variant: 'link', 
                          color: '#D1D5DB', 
                          hover_color: '#FDE047', 
                          icon: '' 
                        })}
                        className="bg-white/5 hover:bg-white/10 text-white border border-white/10"
                      >
                        Ajouter un élément
                      </Button>
                    </div>
                    
                    <div className="space-y-4">
                      {fields.map((field, index) => (
                        <div key={field.id} className="p-4 border border-white/10 rounded-lg">
                          <div className="flex justify-between items-center mb-4">
                            <h4 className="font-medium text-gray-300">Élément {index + 1}</h4>
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              onClick={() => remove(index)}
                              className="bg-red-500/80 hover:bg-red-500 rounded-xl text-white"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <FormField
                              control={form.control}
                              name={`menu_items.${index}.label`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-200">Texte du lien</FormLabel>
                                  <FormControl>
                                    <Input 
                                      {...field} 
                                      className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                                    />
                                  </FormControl>
                                  <FormMessage className="text-red-400" />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name={`menu_items.${index}.href`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-200">URL du lien</FormLabel>
                                  <FormControl>
                                    <Input 
                                      {...field} 
                                      className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                                    />
                                  </FormControl>
                                  <FormMessage className="text-red-400" />
                                </FormItem>
                              )}
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <FormField
                              control={form.control}
                              name={`menu_items.${index}.variant`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-200">Style</FormLabel>
                                  <FormControl>
                                    <select 
                                      {...register(`menu_items.${index}.variant`)} 
                                      className="w-full bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50 rounded-md"
                                    >
                                      <option value="link">Lien</option>
                                      <option value="button">Bouton</option>
                                    </select>
                                  </FormControl>
                                  <FormMessage className="text-red-400" />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name={`menu_items.${index}.color`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-200">Couleur</FormLabel>
                                  <FormControl>
                                    <div className="flex items-center space-x-2">
                                      <Input 
                                        type="color" 
                                        {...field} 
                                        className="w-20 h-10 bg-white/5 border-white/10" 
                                      />
                                      <Input 
                                        {...field} 
                                        className="flex-1 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                      />
                                    </div>
                                  </FormControl>
                                  <FormMessage className="text-red-400" />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name={`menu_items.${index}.hover_color`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-200">Couleur au survol</FormLabel>
                                  <FormControl>
                                    <div className="flex items-center space-x-2">
                                      <Input 
                                        type="color" 
                                        {...field} 
                                        className="w-20 h-10 bg-white/5 border-white/10" 
                                      />
                                      <Input 
                                        {...field} 
                                        className="flex-1 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                                      />
                                    </div>
                                  </FormControl>
                                  <FormMessage className="text-red-400" />
                                </FormItem>
                              )}
                            />
                          </div>

                          <FormField
                            control={form.control}
                            name={`menu_items.${index}.icon`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-200">Icône</FormLabel>
                                <FormControl>
                                  <Input 
                                    {...field} 
                                    className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                                  />
                                </FormControl>
                                <FormMessage className="text-red-400" />
                              </FormItem>
                            )}
                          />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-medium mb-4 text-gray-200">Image de couverture</h3>
                    <FormField
                      control={form.control}
                      name="cover"
                      render={({ field: { value, onChange, ...fieldProps } }) => (
                        <FormItem>
                          <FormControl>
                            <div className="space-y-4">
                              <div className="flex items-center gap-4">
                                <Input
                                  type="file"
                                  id="cover-upload"
                                  className="hidden"
                                  accept=".jpg,.jpeg,.png,.webp"
                                  onChange={(e) => {
                                    onChange(e.target.files);
                                    handleImageChange(e);
                                  }}
                                  {...fieldProps}
                                />
                                <label
                                  htmlFor="cover-upload"
                                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-white bg-white/5 border border-white/10 hover:bg-white/10 transition-colors cursor-pointer"
                                >
                                  <Image className="h-5 w-5" />
                                  Choisir une image
                                </label>
                              </div>
                              {imagePreview ? (
                                <div className="relative aspect-video rounded-xl overflow-hidden group border border-white/10">
                                  <img
                                    src={imagePreview}
                                    alt="Aperçu"
                                    className="object-cover w-full h-full"
                                  />
                                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity" />
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    size="icon"
                                    className="absolute top-2 right-2 p-2 bg-red-500/80 hover:bg-red-500 rounded-xl text-white transition-colors opacity-0 group-hover:opacity-100"
                                    onClick={handleDeleteImage}
                                    disabled={isDeleting}
                                  >
                                    {isDeleting ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <X className="h-4 w-4" />
                                    )}
                                  </Button>
                                </div>
                              ) : (
                                <div className="aspect-video rounded-xl border-2 border-dashed border-white/10 bg-white/5 flex items-center justify-center">
                                  <Image className="h-10 w-10 text-gray-400" />
                                </div>
                              )}
                            </div>
                          </FormControl>
                          <FormDescription className="text-gray-400 text-sm">
                            Image principale du concours. Formats acceptés : JPG, PNG, WebP. Taille max : 5MB.
                          </FormDescription>
                          <FormMessage className="text-red-400" />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>

                <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-medium mb-4 text-gray-200">Logo</h3>
                    <FormField
                      control={form.control}
                      name="logo"
                      render={({ field: { value, onChange, ...fieldProps } }) => (
                        <FormItem>
                          <FormControl>
                            <div className="space-y-4">
                              <div className="flex items-center gap-4">
                                <Input
                                  type="file"
                                  id="logo-upload"
                                  className="hidden"
                                  accept=".jpg,.jpeg,.png,.webp"
                                  onChange={(e) => {
                                    onChange(e.target.files);
                                    handleLogoChange(e);
                                  }}
                                  {...fieldProps}
                                />
                                <label
                                  htmlFor="logo-upload"
                                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-white bg-white/5 border border-white/10 hover:bg-white/10 transition-colors cursor-pointer"
                                >
                                  <Image className="h-5 w-5" />
                                  Choisir un logo
                                </label>
                              </div>
                              {logoPreview ? (
                                <div className="relative aspect-square w-40 h-40 rounded-xl overflow-hidden group border border-white/10">
                                  <img
                                    src={logoPreview}
                                    alt="Aperçu du logo"
                                    className="object-cover w-full h-full"
                                  />
                                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity" />
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    size="icon"
                                    className="absolute top-2 right-2 p-2 bg-red-500/80 hover:bg-red-500 rounded-xl text-white transition-colors opacity-0 group-hover:opacity-100"
                                    onClick={handleDeleteLogo}
                                    disabled={isDeleting}
                                  >
                                    {isDeleting ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <X className="h-4 w-4" />
                                    )}
                                  </Button>
                                </div>
                              ) : (
                                <div className="aspect-square w-40 h-40 rounded-xl border-2 border-dashed border-white/10 bg-white/5 flex items-center justify-center">
                                  <Image className="h-10 w-10 text-gray-400" />
                                </div>
                              )}
                            </div>
                          </FormControl>
                          <FormDescription className="text-gray-400 text-sm">
                            Logo du concours. Formats acceptés : JPG, PNG, WebP. Taille max : 5MB.
                          </FormDescription>
                          <FormMessage className="text-red-400" />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>

                <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-medium mb-4 text-gray-200">Logos des partenaires</h3>
                    <div className="flex gap-2 mb-4">
                      <Input
                        type="text"
                        placeholder="URL du logo ou glisser-déposer un fichier"
                        value={typeof partnerLogoInput === 'string' ? partnerLogoInput : ''}
                        onChange={e => setPartnerLogoInput(e.target.value)}
                        className="bg-white/5 border-white/10 text-white placeholder-gray-400"
                      />
                      <Input
                        type="file"
                        accept={ACCEPTED_IMAGE_TYPES.join(',')}
                        multiple
                        onChange={e => {
                          if (e.target.files && e.target.files.length > 1) {
                            setPartnerLogoInput(Array.from(e.target.files));
                          } else if (e.target.files && e.target.files.length === 1) {
                            setPartnerLogoInput(e.target.files[0]);
                          }
                        }}
                        className="bg-white/5 border-white/10 text-white"
                      />
                      <Button 
                        type="button" 
                        onClick={addPartnerLogo} 
                        className="bg-yellow-500 hover:bg-yellow-600 text-black"
                        disabled={isUploading || !partnerLogoInput}
                      >
                        {isUploading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Chargement...
                          </>
                        ) : (
                          'Ajouter'
                        )}
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-4">
                      {(form.watch('partner_logos') || []).length > 0 ? (
                        form.watch('partner_logos').map((logo: string, idx: number, arr: string[]) => (
                          <div key={logo + idx} className="relative w-24 h-24 border border-white/10 rounded-xl overflow-hidden bg-white/10 flex items-center justify-center group">
                            <img src={logo} alt="Logo partenaire" className="w-full h-full object-contain" />
                            <button type="button" onClick={() => removePartnerLogo(idx)} className="absolute top-1 right-1 bg-red-500/80 hover:bg-red-500 rounded-full p-1 text-white">
                              <X className="h-4 w-4" />
                            </button>
                            <div className="absolute bottom-1 left-1 flex gap-1 opacity-80 group-hover:opacity-100">
                              <button
                                type="button"
                                disabled={idx === 0}
                                onClick={() => {
                                  const logos = [...form.getValues('partner_logos')];
                                  if (idx > 0) {
                                    [logos[idx - 1], logos[idx]] = [logos[idx], logos[idx - 1]];
                                    form.setValue('partner_logos', logos);
                                  }
                                }}
                                className={`bg-gray-700/80 hover:bg-yellow-400 text-white hover:text-black rounded p-1 ${idx === 0 ? 'opacity-40 cursor-not-allowed' : ''}`}
                                title="Monter"
                              >
                                <ArrowLeftIcon className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                disabled={idx === arr.length - 1}
                                onClick={() => {
                                  const logos = [...form.getValues('partner_logos')];
                                  if (idx < logos.length - 1) {
                                    [logos[idx + 1], logos[idx]] = [logos[idx], logos[idx + 1]];
                                    form.setValue('partner_logos', logos);
                                  }
                                }}
                                className={`bg-gray-700/80 hover:bg-yellow-400 text-white hover:text-black rounded p-1 ${idx === arr.length - 1 ? 'opacity-40 cursor-not-allowed' : ''}`}
                                title="Descendre"
                              >
                                <ArrowRightIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <span className="text-gray-400 text-sm">Aucun logo de partenaire ajouté</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </AdminLayout>
  );
};

export default EditContestPage;